import { isObject, freezeObject, isFunction } from '@/global/utils/utils'
import { computed, ref } from 'vue'
import { api } from '@/global/services/api'
import { registeredTableHandlers } from '@/global/components/view-layouts/tabs-layout/config/registered-table-handlers'
import { showDetailsAction, deleteAction } from '@/global/components/table/helpers/tableActions.js'

const UI_VIEW = 'temporary_regulations_work_orders'
const TABLE_KEY = 'work_orders'

export function useTable (options) {
  const {
    trans,
    router,
    displayDatetimeFormat,
    openInfoDialog,
    closeInfoDialog,
    setInfoDialogLoaderFn
  } = options

  const isCreating = ref(false)

  const resetCreating = () => {
    isCreating.value = false
  }

  const apiConfig = freezeObject({
    get: {
      module: 'road-maintenance',
      route: 'projects'
    }
  })

  // TODO: uncomment this later if create is needed
  // const startCreating = () => {
  //   isCreating.value = true
  // }

  // const onCreateProject = () => {
  //   startCreating()
  // }

  const onShowProjectDetails = (event) => {
    const { id } = event?.data || {}
    if (!id) {
      return
    }

    router.push({
      name: 'TemporaryRegulationsWorkOrderDetails',
      params: {
        id
      }
    })
  }

  const deleteProjectHandler = async (event) => {
    try {
      const { id } = event?.data || {}
      setInfoDialogLoaderFn(true)
      if (!id) {
        return
      }

      await api('road-maintenance').delete(`projects/${id}`)
      closeInfoDialog()
      const { fetchData } = registeredTableHandlers?.[TABLE_KEY] || {}

      if (isFunction(fetchData)) {
        fetchData()
      }
    }
    catch (exception) {
      console.error(exception)
    }
    finally {
      setInfoDialogLoaderFn(false)
    }
  }

  const onDeleteProject = (event) => {
    if (!isFunction(openInfoDialog)) {
      return
    }

    openInfoDialog({
      title: trans('road-maintenance/temporary_regulation.delete_traffic_regulation') + (event?.data?.name ? ' - ' + event.data.name : ''),
      message: trans('road-maintenance/temporary_regulation.delete_traffic_regulation_message'),
      onConfirm: () => deleteProjectHandler(event)
    })
  }

  const paginationConfig = freezeObject({
    perPageOptions: [50, 100],
    perPage: 50,
    currentPage: 1
  })

  const actionsConfig = computed(() => ([
    showDetailsAction(trans, onShowProjectDetails),
    deleteAction(trans, onDeleteProject)
  ]))

  const breadcrumbs = computed(() => {
    const baseItems = [
      {
        text: trans('road-maintenance.temporary_regulation')
      },
      {
        text: trans('road-maintenance.prp_rn'),
        bolded: true
      }
    ]

    if (isCreating.value) {
      baseItems[1].bolded = false
      baseItems.push({
        text: trans('road-maintenance/temporary_regulation.adding_new_regulation'),
        bolded: true
      })
    }

    return {
      items: baseItems
    }
  })

  const cellsConfig = computed(() => ({
    status: {
      type: 'status',
      custom_display_value: (data) => {
        if (!data?.status || !isObject(data.status)) {
          return
        }

        return {
          ...data.status,
          ...{
            description: trans(data?.status?.description || '')
          }
        }
      },
      displayValue: 'description',
      colorObjectKey: 'color',
      textColorKey: 'text',
      backgroundColorKey: 'background'
    }
  }))

  const headers = computed(() => ([
    {
      label: trans('road-maintenance/settings.id'),
      key: 'id',
      always_visible: true
    },
    {
      label: trans('road-maintenance/temporary_regulation/requests.eregulation_number'),
      key: 'traffic_regulation_code',
      always_visible: true
    },
    {
      label: trans('road-maintenance/work_orders.work_order_creation_date'),
      key: 'created_at',
      always_visible: true
    },
    {
      label: trans('road-maintenance/work_orders.start_date'),
      key: 'planned_start',
      always_visible: true
    },
    {
      label: trans('road-maintenance/work_orders.end_date'),
      key: 'planned_end',
      always_visible: true
    },
    {
      label: trans('road-maintenance/work_orders.addresses'),
      key: 'address',
      always_visible: false,
      initial_value: true
    },
    {
      label: trans('road-maintenance/excavation-service.status'),
      key: 'status',
      always_visible: false,
      initial_value: true
    }
  ]))

  const filtersConfig = computed(() => ({
    fields: [
      {
        key: 'id',
        label: trans('road-maintenance/settings.id')
      },
      {
        key: 'created_at',
        label: trans('road-maintenance/work_orders.work_order_creation_date'),
        type: 'datetime-picker',
        subType: 'date',
        appendIcon: 'mdi-calendar-month',
        format: displayDatetimeFormat,
        placeholder: displayDatetimeFormat
      },
      {
        key: 'planned_start',
        label: trans('road-maintenance/work_orders.start_date'),
        type: 'datetime-picker',
        subType: 'date',
        appendIcon: 'mdi-calendar-month',
        format: displayDatetimeFormat,
        placeholder: displayDatetimeFormat
      },
      {
        key: 'planned_end',
        label: trans('road-maintenance/work_orders.end_date'),
        type: 'datetime-picker',
        subType: 'date',
        appendIcon: 'mdi-calendar-month',
        format: displayDatetimeFormat,
        placeholder: displayDatetimeFormat
      },
      {
        key: 'address',
        label: trans('road-maintenance/aggregator.address')
      },
      {
        key: 'status',
        label: trans('road-maintenance/excavation-service.status'),
        type: 'autocomplete',
        autocomplete_options: {
          module: 'fleet',
          route: 'travel-documents/statuses',
          custom_display_value: (value) => {
            return trans(value?.description || '')
          },
          form_value: 'id'
        }
      }
    ]
  }))

  const config = computed(() => ({
    tableConfig: {
      key: TABLE_KEY,
      uiView: UI_VIEW,
      apiConfig,
      rowDblClick: onShowProjectDetails,
      tableConfig: {
        cellsConfig: cellsConfig.value,
        headers: headers.value,
        actionsConfig: actionsConfig.value,
        filtersConfig: filtersConfig.value,
        paginationConfig
      }
    },
    breadcrumbs: breadcrumbs.value
  }))

  return {
    config,
    isCreating,
    resetCreating
  }
}
